/**
 * useTutorLiveChromeChat — le chat du live, vu du masteur : « Tous » et un fil par élève rattaché à la
 * salle (`tutorChatChannels`), sur le modèle du chat de l'élève (`engine/live/student/useLiveChat`,
 * `ports/…/student/Live/useLiveChatAttachments`) et des services de l'ancien chat
 * (`common/liveMode/LiveChat.js`, `common/messenger/CommonChat.js`).
 *
 * D'où il lit (décision 4 : les tranches que l'écouteur global `LKEvents` écrit) :
 *  - le fil relu : `currentUser.messages[fil]`, rangé par le réducteur de `getMessages` ;
 *  - un message reçu : `currentUser.new_message` (l'événement `newMessage` d'un autre, que l'écouteur
 *    range et dont il joue le son, `useLiveKitEventListener.js:111-130`) — ajouté au fil s'il est le sien ;
 *  - « … écrit » : `liveMode.isTyping` / `liveMode.typingUser` (le `typing` d'un autre) ;
 *  - les non-lus de chaque puce : la séance (`unread_private_messages_count`, `unread_messages_count`).
 *
 * Ce qu'il envoie (`tutorLiveChromeProtocol`) : le texte (`postMessages`, puis `newMessage` à l'élève du
 * fil ou à tous), la frappe (`typing`, au plus une fois par `TYPING_THROTTLE_MS`), une photo ou un
 * fichier (`postImage` / `postDocument`, puis la ressource annoncée), un vocal (la file des vocaux de la
 * messagerie, `postAudio`, puis sa ressource annoncée), les messages plus anciens (`getPageMessages`).
 *
 * Ce qu'il fait d'une pièce choisie (`CommonChat.js:1057-1205`) : dans le fil d'un élève, l'aperçu
 * « Nouveau fichier » (« Créer une activité » ou « Envoyer dans le chat ») ; dans « Tous », la boîte
 * « Crée plutôt une activité » (« Créer l'activité » : l'activité commune, ou « Envoyer »). Une pièce
 * reçue touchée : l'activité qui en est née, si elle existe (dans le fil d'un élève, sa liste ; dans
 * « Tous », `getActivityFromChat`), sinon l'aperçu (fil d'un élève, flèches entre les photos) ou la
 * visionneuse (« Tous »).
 *
 * Ouvert avec des non-lus, le fil est dit lu (`mark-read`) et son compte remis à zéro 3 s plus tard
 * (`CommonChat.js:997-1013`). Au retour dans l'app, le fil ouvert se relit et les notifications
 * « messages » s'effacent (`useTutorLiveChromeChatResume`).
 *
 * Chaque puce d'élève dit s'il est dans la salle et s'il a levé la main (`liveMode.liveParticipants`,
 * `liveMode.raisedHands`, tenus par l'écouteur global), comme l'avatar de l'ancien chat (`LiveAvatarM`).
 */
import { useIsFocused, useNavigation } from "@react-navigation/native"
import * as DocumentPicker from "expo-document-picker"
import * as ImagePicker from "expo-image-picker"
import moment from "moment"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Linking } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { changeUnreadMessageInLiveCount } from "../../../../../../data/redux/actions/broadcast"
import { clearLastPhoto } from "../../../../../../data/redux/actions/camera"
import { type ComposerAttachSource } from "../../../../../../design-system/molecules/MessageComposer"
import { type LiveChatProps } from "../../../../../../design-system/organisms/LiveChat"
import { type UITutorLiveDialog } from "../../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"
import {
    TYPING_THROTTLE_MS,
    messageOfEvent,
    uncoveredReceived,
    voiceMessages,
    type LiveChatMessage,
} from "../../../../../../engine/live/student/useLiveChat"
import { type LiveDataEvent } from "../../../../../../engine/live/room/dataChannel"
import { apiFailureOf } from "../../../../../../engine/shared/apiCall"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { useVoiceOutbox, type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import {
    TUTOR_GROUP_CHANNEL,
    tutorChatChannelOfThread,
    tutorChatChannelOptions,
    tutorChatChannels,
    tutorLiveStudentsOf,
    type ApiTutorLiveMember,
    type ApiTutorLiveSession,
} from "../../../../adapters/tutor/Live/tutorLiveChatAdapter"
import { avatarOf } from "../../../../adapters/student/Live/studentLiveAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import {
    tutorChatChannelsInRoom,
    tutorChatDays,
    tutorChatTyping,
    type TutorChatRoomReading,
} from "../../../../view-models/tutor/Live/tutorLiveChatViewModel"
import { type UIConversationMessage, type UIMessageLink } from "../../../types/studentConversation"
import { type UITutorMediaPreview } from "../../../types/tutorEleves"
import {
    attachmentForm,
    documentPart,
    imagePart,
    isPdfFile,
    uploadPart,
} from "../../student/Messaging/conversationFiles"
import { linkHref, phoneHref } from "../../student/Messaging/conversationLinks"
import { messengerVoiceFileName, messengerVoiceOutbox } from "../../student/Messaging/messengerVoiceOutbox"
import { openConversationLink } from "../../student/Messaging/openConversationLink"
import { useConversationVoicePlayer } from "../../student/Messaging/useConversationVoicePlayer"
import {
    CONVERSATION_ATTACH_SOURCES,
    viewerFor,
    type ConversationViewer,
} from "../../student/Messaging/useStudentTutorConversationState"
import { useTutorLiveChromeChatResume } from "./useTutorLiveChromeChatResume"
import { type TutorLiveChromeProtocol } from "./useTutorLiveChromeProtocol"

/** Le `formId` que l'appareil photo legacy pose sur une photo du chat, celui de la messagerie. */
const CAMERA_FORM_ID = "chat"
/** Le délai de l'ancien chat avant de remettre les non-lus du fil ouvert à zéro. */
export const UNREAD_RESET_MS = 3000

/** Un fichier choisi, avant son envoi : d'où il vient (le mot de la boîte de « Tous »), où il va. */
export type TutorChatPickedFile = {
    uri: string
    name?: string
    mimeType?: string
    kind: "image" | "document"
    media: "photo" | "image" | "file"
    channel: string
    threadId: string
}

/** Ce que la génération reçoit d'un fichier du chat : pour qui, et la pièce. */
export type TutorLiveGenerateRequest = {
    /** Les élèves visés ; vide : tout le groupe. */
    studentIds: string[]
    attachment?: { uri: string; name?: string; mimeType?: string; kind: "image" | "document" }
    /** Une pièce du fil : l'heure de son message (ms), que le brouillon écrit à côté de « Photo du chat ». */
    at?: number
}

/** L'aperçu d'une pièce : un fichier neuf, ou une pièce du fil d'un élève sans activité. */
export type TutorChatPreview = {
    media: UITutorMediaPreview
    fresh: boolean
    position: { index: number; count: number } | null
    sending: boolean
    onClose: () => void
    onCreateActivity: () => void
    onSendToChat?: () => void
    onPrevious?: () => void
    onNext?: () => void
}

type AccountState = {
    id?: number | string | null
    display_name?: string | null
    messages?: Record<string, unknown>
    new_message?: Record<string, unknown> | null
}

type RootState = {
    liveMode?: { isTyping?: boolean; typingUser?: number | string | null } & TutorChatRoomReading
    camera?: { lastPhoto?: { uri?: string } | null; formId?: string | null }
}

type Paging = { nextPageId: string | number | null; finalPage: boolean }
type MessengerPage = { data?: unknown; meta?: { next_page_id?: string | number | null; final_page?: boolean } } | null

const pagingOf = (page: MessengerPage | undefined): Paging => ({
    nextPageId: page?.meta?.next_page_id ?? null,
    finalPage: page?.meta?.final_page !== false,
})

const messageList = (raw: unknown): LiveChatMessage[] =>
    Array.isArray(raw) ? (raw as LiveChatMessage[]).filter((item) => item != null) : []

type Unsent = { kind: "text"; text: string } | { kind: "image" | "document"; body: unknown }

const NO_MESSAGES: LiveChatMessage[] = []

export type UseTutorLiveChromeChatArgs = {
    session: ApiTutorLiveSession | null
    open: boolean
    /** Le canal choisi (`selection.chat.channel`) ; absent : « Tous ». */
    channel: string | undefined
    onChangeChannel: (channel: string) => void
    onClose: () => void
    protocol: TutorLiveChromeProtocol
    telemetry: EngineTelemetry
    apiUrl: string
    /** Une activité née d'un message : elle s'ouvre au centre. */
    onOpenActivity: (studentId: string, activityId: string) => void
    /** « Créer une activité » depuis une pièce : la génération, pour ces élèves, sur ce fichier. */
    onGenerate: (request: TutorLiveGenerateRequest) => void
}

export type TutorLiveChromeChat = {
    /** Les props de `<LiveChat viewer="tutor">`, chat ouvert ; `null` fermé. */
    props: Omit<LiveChatProps, "viewer" | "tutor"> | null
    /** La boîte « Crée plutôt une activité » d'une pièce choisie dans « Tous ». */
    dialog: { value: UITutorLiveDialog; onConfirm: () => void; onCancel: () => void; onDismiss: () => void } | null
    preview: TutorChatPreview | null
    viewer: ConversationViewer | null
    closeViewer: () => void
    recorder: { threadId: string | null; onClose: () => void; onSend: (file: VoiceFile) => void } | null
}

export function useTutorLiveChromeChat(args: UseTutorLiveChromeChatArgs): TutorLiveChromeChat {
    const { session, open, onChangeChannel, onClose, protocol, telemetry, apiUrl } = args
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as { navigate: (route: string, params?: object) => void }
    const isFocused = useIsFocused()
    const account = useSelector((state: unknown) => selectAccount(state as never) as AccountState | null)
    const isTyping = useSelector((state: RootState) => state.liveMode?.isTyping === true)
    const typingUser = useSelector((state: RootState) => state.liveMode?.typingUser ?? null)
    const liveParticipants = useSelector((state: RootState) => state.liveMode?.liveParticipants)
    const raisedHands = useSelector((state: RootState) => state.liveMode?.raisedHands)
    const lastPhoto = useSelector((state: RootState) => state.camera?.lastPhoto)
    const lastPhotoFormId = useSelector((state: RootState) => state.camera?.formId)

    const entries = useMemo(() => tutorChatChannels(session), [session])
    const roster = useMemo<ApiTutorLiveMember[]>(() => tutorLiveStudentsOf(session), [session])
    // Un élève parti du roster rend son canal : « Tous ».
    const channel =
        args.channel != null && entries.some((entry) => entry.channel === args.channel)
            ? args.channel
            : TUTOR_GROUP_CHANNEL
    const entry = entries.find((item) => item.channel === channel)
    const threadId = entry?.threadId ?? null
    const unread = entry?.unread ?? 0

    const server = threadId != null ? account?.messages?.[threadId] : undefined
    const newMessage = account?.new_message ?? null
    const selfId = account?.id
    const user = useMemo(() => ({ id: account?.id, name: account?.display_name }), [account?.id, account?.display_name])

    const [received, setReceived] = useState<LiveChatMessage[]>([])
    const [pending, setPending] = useState<LiveChatMessage[]>([])
    const [drafts, setDrafts] = useState<Record<string, string>>({})
    const [sending, setSending] = useState(false)
    const [attaching, setAttaching] = useState(false)
    const [paging, setPaging] = useState<Record<string, Paging>>({})
    const [settled, setSettled] = useState<Record<string, true>>({})
    const [loadingEarlier, setLoadingEarlier] = useState(false)
    const [picked, setPicked] = useState<TutorChatPickedFile | null>(null)
    const [shown, setShown] = useState<{ messageId: string } | null>(null)
    const [viewer, setViewer] = useState<ConversationViewer | null>(null)
    const [recorder, setRecorder] = useState<{ channel: string; threadId: string | null } | null>(null)
    const unsentRef = useRef(new Map<string, Unsent>())
    const lastTypingRef = useRef(0)
    const cameraTargetRef = useRef<{ channel: string; threadId: string } | null>(null)
    const openRef = useRef(open)
    openRef.current = open
    const threadRef = useRef(threadId)
    threadRef.current = threadId
    const draft = threadId != null ? (drafts[threadId] ?? "") : ""
    const voiceQueue = useVoiceOutbox(messengerVoiceOutbox)
    const voice = useConversationVoicePlayer(threadId)

    const refresh = useCallback(
        (thread: string) =>
            protocol.readThread<MessengerPage>(thread).then((page) => {
                const relu = messageList(page?.data)
                setReceived((list) => [
                    ...list.filter((message) => String(message.thread_id) !== thread),
                    ...uncoveredReceived(
                        list.filter((message) => String(message.thread_id) === thread),
                        relu
                    ),
                ])
                setPaging((all) => ({ ...all, [thread]: pagingOf(page ?? undefined) }))
            }),
        [protocol]
    )

    const markRead = useCallback(
        (thread: string) => {
            protocol.markRead(thread).catch((cause: unknown) => {
                telemetry.error("chat_mark_read", cause)
            })
        },
        [protocol, telemetry]
    )

    // Changer de canal (ou ouvrir le chat) : le fil relu, puis dit lu (`CommonChat.js:226-272`). Une
    // fois par ouverture et par fil : les gestes se lisent au moment, un nouveau rendu ne relance rien.
    const readRef = useRef({ refresh, markRead, telemetry })
    readRef.current = { refresh, markRead, telemetry }
    useEffect(() => {
        if (!open || threadId == null) return
        const read = readRef.current
        read.refresh(threadId)
            .then(() => {
                if (openRef.current && threadRef.current === threadId) readRef.current.markRead(threadId)
            })
            .catch((cause: unknown) => {
                read.telemetry.error("chat_messages", cause)
            })
            .finally(() => setSettled((all) => (all[threadId] ? all : { ...all, [threadId]: true })))
    }, [open, threadId])

    // Au retour dans l'app : le fil relu, les notifications « messages » effacées (`CommonChat.js:173-190`).
    useTutorLiveChromeChatResume({ open, threadId, refresh, telemetry })

    // Ouvert avec des non-lus : 3 s plus tard, le fil est dit lu et son compte remis à zéro.
    useEffect(() => {
        if (!open || threadId == null || unread <= 0) return undefined
        const timer = setTimeout(() => {
            markRead(threadId)
            dispatch(changeUnreadMessageInLiveCount(threadId, null, 0) as never)
        }, UNREAD_RESET_MS)
        return () => clearTimeout(timer)
    }, [open, threadId, unread, markRead, dispatch])

    // Un message reçu par la salle (`new_message`, posé par l'écouteur global) entre dans son fil.
    useEffect(() => {
        if (newMessage == null) return
        const message = messageOfEvent(newMessage as LiveDataEvent)
        if (message == null) return
        setReceived((list) => (list.some((item) => item.id === message.id) ? list : [...list, message]))
    }, [newMessage])

    const setDraft = useCallback(
        (text: string) => {
            if (threadId == null) return
            setDrafts((all) => ({ ...all, [threadId]: text }))
            if (text === "") return
            const now = Date.now()
            if (now - lastTypingRef.current < TYPING_THROTTLE_MS) return
            lastTypingRef.current = now
            void protocol.typing(channel)
        },
        [threadId, protocol, channel]
    )

    const markFailed = useCallback((temporaryId: string, unsent: Unsent, cause: unknown) => {
        unsentRef.current.set(temporaryId, unsent)
        const failure = apiFailureOf(cause) ?? undefined
        setPending((list) =>
            list.map((message) =>
                message.id === temporaryId
                    ? { ...message, status: "failed", ...(failure != null ? { failure } : {}) }
                    : message
            )
        )
    }, [])

    /** Le serveur a rangé l'envoi : il prend sa forme, et la relecture du fil l'écarte. */
    const markStored = useCallback(
        (temporaryId: string, resource: Record<string, unknown> | null, thread: string) => {
            unsentRef.current.delete(temporaryId)
            const saved = resource != null ? messageOfEvent(resource as LiveDataEvent) : null
            const id = saved?.id ?? temporaryId
            setPending((list) =>
                list.map((message) =>
                    message.id !== temporaryId
                        ? message
                        : saved == null
                          ? { ...message, status: undefined, failure: undefined }
                          : { ...saved, thread_id: saved.thread_id ?? thread }
                )
            )
            refresh(thread)
                .then(() => setPending((list) => list.filter((message) => message.id !== id)))
                .catch((cause: unknown) => {
                    telemetry.error("chat_messages", cause)
                })
        },
        [refresh, telemetry]
    )

    const postText = useCallback(
        (temporaryId: string, target: { channel: string; threadId: string }, text: string, createdAt: string) => {
            setSending(true)
            protocol
                .sendText({ ...target, temporaryId, text, createdAt, user })
                .then(
                    (resource) => markStored(temporaryId, resource, target.threadId),
                    (cause: unknown) => {
                        telemetry.error("chat_send", cause)
                        markFailed(temporaryId, { kind: "text", text }, cause)
                    }
                )
                .finally(() => setSending(false))
        },
        [protocol, user, markStored, markFailed, telemetry]
    )

    const postFile = useCallback(
        (
            temporaryId: string,
            target: { channel: string; threadId: string },
            kind: "image" | "document",
            body: unknown
        ) => {
            setAttaching(true)
            protocol
                .sendAttachment({ ...target, kind, body })
                .then(
                    (resource) => markStored(temporaryId, resource, target.threadId),
                    (cause: unknown) => {
                        telemetry.error("chat_attachment", cause, { kind })
                        markFailed(temporaryId, { kind, body }, cause)
                    }
                )
                .finally(() => setAttaching(false))
        },
        [protocol, markStored, markFailed, telemetry]
    )

    const sendMessage = useCallback(() => {
        const text = draft.trim()
        if (text === "" || threadId == null) return
        const temporaryId = `pending-${Date.now()}`
        const createdAt = moment().toISOString()
        setPending((list) => [
            ...list,
            {
                id: temporaryId,
                thread_id: threadId,
                owner_id: selfId ?? undefined,
                body: text,
                created_at: createdAt,
                status: "sending",
            },
        ])
        setDrafts((all) => ({ ...all, [threadId]: "" }))
        postText(temporaryId, { channel, threadId }, text, createdAt)
    }, [draft, threadId, channel, selfId, postText])

    /** Une pièce part dans le fil où son geste a commencé. */
    const sendFile = useCallback(
        async (file: TutorChatPickedFile) => {
            const part =
                file.kind === "image"
                    ? await uploadPart(imagePart(file.uri, account?.display_name))
                    : await uploadPart(documentPart({ uri: file.uri, name: file.name, mimeType: file.mimeType }))
            if (part == null) return
            const temporaryId = `pending-${file.kind}-${Date.now()}`
            setPending((list) => [
                ...list,
                {
                    id: temporaryId,
                    thread_id: file.threadId,
                    owner_id: selfId ?? undefined,
                    created_at: moment().toISOString(),
                    status: "sending",
                    ...(file.kind === "image"
                        ? { image: { lg: file.uri } }
                        : { document: file.uri, document_name: file.name ?? null }),
                },
            ])
            postFile(
                temporaryId,
                { channel: file.channel, threadId: file.threadId },
                file.kind,
                attachmentForm(file.kind, part)
            )
        },
        [account?.display_name, selfId, postFile]
    )

    const retry = useCallback(
        (messageId: string) => {
            if (voiceQueue.items.some((item) => item.id === messageId)) {
                messengerVoiceOutbox.retry(messageId)
                return
            }
            const unsent = unsentRef.current.get(messageId)
            const message = pending.find((item) => item.id === messageId && item.status === "failed")
            if (unsent == null || message?.thread_id == null) return
            const thread = String(message.thread_id)
            const target = {
                channel: tutorChatChannelOfThread(session, thread) ?? TUTOR_GROUP_CHANNEL,
                threadId: thread,
            }
            setPending((list) =>
                list.map((item) => (item.id === messageId ? { ...item, status: "sending", failure: undefined } : item))
            )
            if (unsent.kind === "text")
                postText(messageId, target, unsent.text, message.created_at ?? moment().toISOString())
            else postFile(messageId, target, unsent.kind, unsent.body)
        },
        [voiceQueue.items, pending, session, postText, postFile]
    )

    // Un fichier choisi : l'aperçu dans le fil d'un élève, la boîte « Crée plutôt une activité » dans « Tous ».
    const choose = useCallback((file: TutorChatPickedFile) => setPicked(file), [])

    // La photo de l'appareil revient par le magasin ; seul le live au premier plan la prend.
    useEffect(() => {
        const target = cameraTargetRef.current
        if (!isFocused || target == null || lastPhotoFormId !== CAMERA_FORM_ID || lastPhoto?.uri == null) return
        const uri = lastPhoto.uri
        cameraTargetRef.current = null
        dispatch(clearLastPhoto() as never)
        choose({ uri, kind: "image", media: "photo", ...target })
    }, [dispatch, isFocused, lastPhoto, lastPhotoFormId, choose])

    const attach = useCallback(
        async (source: ComposerAttachSource) => {
            if (threadId == null) return
            const target = { channel, threadId }
            if (source === "voice") {
                voice.stop()
                setRecorder({ channel, threadId })
                return
            }
            if (source === "camera") {
                cameraTargetRef.current = target
                navigation.navigate("camera", { formId: CAMERA_FORM_ID })
                return
            }
            if (source === "gallery") {
                const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ["images"], quality: 1 })
                const asset = result.canceled ? null : result.assets?.[0]
                if (asset?.uri != null) choose({ uri: asset.uri, kind: "image", media: "image", ...target })
                return
            }
            const result = await DocumentPicker.getDocumentAsync({ type: ["application/pdf", "image/*"] })
            const asset = result.canceled ? null : result.assets?.[0]
            if (asset?.uri == null) return
            const pdf = isPdfFile(asset)
            choose({
                uri: asset.uri,
                name: asset.name ?? undefined,
                mimeType: asset.mimeType ?? undefined,
                kind: pdf ? "document" : "image",
                media: "file",
                ...target,
            })
        },
        [threadId, channel, voice, navigation, choose]
    )

    // Un vocal parti pendant le live s'annonce à l'élève de son fil, ou à tous ; ceux d'avant non.
    const announcedRef = useRef<Set<string> | null>(null)
    if (announcedRef.current == null) {
        announcedRef.current = new Set(
            messengerVoiceOutbox
                .getState()
                .items.filter((item) => item.status === "sent")
                .map((item) => item.id)
        )
    }
    useEffect(() => {
        const announced = announcedRef.current
        if (announced == null) return
        for (const item of voiceQueue.items) {
            if (item.status !== "sent" || announced.has(item.id)) continue
            announced.add(item.id)
            const owner = tutorChatChannelOfThread(session, item.target.threadId)
            if (owner == null) continue
            const resource = item.result as Record<string, unknown> | null
            if (resource != null) void protocol.announceVoice(owner, resource)
            refresh(item.target.threadId).catch((cause: unknown) => {
                telemetry.error("chat_messages", cause)
            })
        }
    }, [voiceQueue.items, session, protocol, refresh, telemetry])

    const loadEarlier = useCallback(() => {
        const threadPaging = threadId != null ? paging[threadId] : undefined
        if (threadId == null || loadingEarlier || threadPaging == null || threadPaging.finalPage) return
        if (threadPaging.nextPageId == null) return
        setLoadingEarlier(true)
        protocol
            .readEarlier<MessengerPage>(threadId, threadPaging.nextPageId)
            .then((page) => setPaging((all) => ({ ...all, [threadId]: pagingOf(page ?? undefined) })))
            .catch((cause: unknown) => {
                telemetry.error("chat_earlier", cause)
            })
            .finally(() => setLoadingEarlier(false))
    }, [threadId, paging, loadingEarlier, protocol, telemetry])

    const messages = useMemo(() => {
        const fromServer = messageList(server)
        const known = new Set(fromServer.map((message) => String(message.id)))
        const live = uncoveredReceived(
            received.filter((message) => String(message.thread_id) === threadId),
            fromServer
        )
        const mine = [
            ...pending.filter((message) => String(message.thread_id) === threadId && !known.has(message.id)),
            ...voiceMessages(voiceQueue.items, threadId, selfId).filter((message) => !known.has(message.id)),
        ]
        return fromServer.length + live.length + mine.length === 0 ? NO_MESSAGES : [...fromServer, ...live, ...mine]
    }, [server, received, pending, threadId, voiceQueue.items, selfId])

    const days = useMemo(
        () => tutorChatDays({ messages, selfId, channel, roster, apiUrl, avatarOf }),
        [messages, selfId, channel, roster, apiUrl]
    )
    const flat = useMemo(() => days.flatMap((day) => day.bursts.flatMap((burst) => burst.messages)), [days])
    const channels = useMemo(
        () => tutorChatChannelsInRoom(tutorChatChannelOptions(session, avatarOf), { liveParticipants, raisedHands }),
        [session, liveParticipants, raisedHands]
    )
    const threadPaging = threadId != null ? paging[threadId] : undefined
    const voiceSending = voiceQueue.items.some((item) => item.target.threadId === threadId && item.status === "sending")
    const loading = open && threadId != null && !Array.isArray(server) && settled[threadId] !== true

    /** Une pièce touchée : l'activité qui en est née, sinon l'aperçu ou la visionneuse. */
    const openAttachment = useCallback(
        (message: UIConversationMessage) => {
            if (message.attachment == null || message.attachment.kind === "voice") return
            const student = roster.find((member) => String(member.id) === channel)
            if (student != null) {
                const linked = linkedActivity(student, message.id)
                if (linked != null) {
                    onClose()
                    args.onOpenActivity(String(student.id), linked)
                    return
                }
                setShown({ messageId: message.id })
                return
            }
            protocol
                .activityFromChat<{ id?: number | string; student_id?: number | string; user_id?: number | string }>(
                    message.id
                )
                .then((activity) => {
                    const owner = activity?.student_id ?? activity?.user_id
                    if (activity?.id == null || owner == null) throw new Error("no_activity")
                    onClose()
                    args.onOpenActivity(String(owner), String(activity.id))
                })
                .catch(() => {
                    const next = viewerFor(message, flat)
                    if (next != null) setViewer(next)
                })
        },
        // `args.onOpenActivity` est lu au geste.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [roster, channel, protocol, flat, onClose]
    )

    const onPressLink = useCallback((link: UIMessageLink) => {
        const href = linkHref(link)
        if (href != null) openConversationLink(href, link.kind)
        else void Linking.openURL(phoneHref(link.value, "call"))
    }, [])

    const studentIdsOf = (target: string): string[] => (target === TUTOR_GROUP_CHANNEL ? [] : [target])

    // L'aperçu : le fichier choisi dans le fil d'un élève, ou la pièce touchée sans activité.
    const preview = useMemo((): TutorChatPreview | null => {
        if (picked != null && picked.channel !== TUTOR_GROUP_CHANNEL) {
            return {
                media: { kind: picked.kind, uri: picked.uri, ...(picked.name != null ? { name: picked.name } : {}) },
                fresh: true,
                position: null,
                sending: false,
                onClose: () => setPicked(null),
                onCreateActivity: () => {
                    setPicked(null)
                    onClose()
                    args.onGenerate({ studentIds: studentIdsOf(picked.channel), attachment: picked })
                },
                onSendToChat: () => {
                    setPicked(null)
                    void sendFile(picked)
                },
            }
        }
        if (shown == null) return null
        const pieces = flat.filter((item) => item.attachment?.kind === "image" || item.attachment?.kind === "document")
        const images = flat.filter((item) => item.attachment?.kind === "image")
        const current = pieces.find((item) => item.id === shown.messageId)
        const attachment = current?.attachment
        if (current == null || attachment == null || attachment.kind === "voice") return null
        const index = images.findIndex((item) => item.id === current.id)
        const step = (delta: number) => () => {
            const next = images[index + delta]
            if (next != null) setShown({ messageId: next.id })
        }
        return {
            media: {
                kind: attachment.kind,
                uri: attachment.uri,
                ...(attachment.kind === "document" && attachment.name != null ? { name: attachment.name } : {}),
            },
            fresh: false,
            position: index >= 0 ? { index, count: images.length } : null,
            sending: false,
            onClose: () => setShown(null),
            onCreateActivity: () => {
                setShown(null)
                onClose()
                const sentAt = current.sentAt != null ? Date.parse(current.sentAt) : NaN
                args.onGenerate({
                    studentIds: studentIdsOf(channel),
                    attachment: { uri: attachment.uri, kind: attachment.kind },
                    ...(Number.isNaN(sentAt) ? {} : { at: sentAt }),
                })
            },
            ...(index >= 0 ? { onPrevious: step(-1), onNext: step(1) } : {}),
        }
        // `args.onGenerate` est lu au geste.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [picked, shown, flat, channel, onClose, sendFile])

    const dialog =
        picked != null && picked.channel === TUTOR_GROUP_CHANNEL
            ? {
                  value: { kind: "chat_attachment" as const, media: picked.media },
                  onConfirm: () => {
                      setPicked(null)
                      onClose()
                      args.onGenerate({ studentIds: [], attachment: picked })
                  },
                  onCancel: () => {
                      setPicked(null)
                      void sendFile(picked)
                  },
                  onDismiss: () => setPicked(null),
              }
            : null

    const props = open
        ? {
              days,
              channels,
              channel,
              onChangeChannel,
              loading,
              readOnly: false,
              draft,
              onChangeDraft: setDraft,
              onSend: sendMessage,
              sending,
              attachSources: threadId != null ? CONVERSATION_ATTACH_SOURCES : [],
              onAttach: (source: ComposerAttachSource) => void attach(source),
              attaching: attaching || voiceSending,
              onOpenAttachment: openAttachment,
              voicePlayback: voice.playback,
              onToggleVoice: voice.toggle,
              onSeekVoice: voice.seek,
              onCycleVoiceRate: voice.cycleRate,
              onPressLink,
              onRetryMessage: (message: UIConversationMessage) => retry(message.id),
              typingLabel: tutorChatTyping({ isTyping, userId: typingUser }, roster),
              hasEarlier: threadPaging != null && !threadPaging.finalPage && threadPaging.nextPageId != null,
              loadingEarlier,
              onLoadEarlier: loadEarlier,
              onClose,
          }
        : null

    return {
        props,
        dialog,
        preview,
        viewer,
        closeViewer: () => setViewer(null),
        recorder:
            recorder != null
                ? {
                      threadId: recorder.threadId,
                      onClose: () => setRecorder(null),
                      onSend: (file: VoiceFile) => {
                          if (recorder.threadId == null) return
                          messengerVoiceOutbox.enqueue(file, {
                              threadId: recorder.threadId,
                              fileName: messengerVoiceFileName(account?.display_name, file.extension),
                          })
                      },
                  }
                : null,
    }
}

type MemberWithActivities = ApiTutorLiveMember & {
    todo_activities?: ({ id?: number | string; message_id?: number | string | null } | null)[] | null
    activities?: ({ id?: number | string; message_id?: number | string | null } | null)[] | null
}

/**
 * L'activité née d'un message, dans les listes de l'élève du fil (`CommonChat.js:593-623` :
 * `todo_activities` puis `activities`, `message_id`).
 */
export function linkedActivity(member: ApiTutorLiveMember, messageId: string): string | null {
    const lists = member as MemberWithActivities
    const found = [...(lists.todo_activities ?? []), ...(lists.activities ?? [])].find(
        (activity) => activity?.message_id != null && String(activity.message_id) === messageId
    )
    return found?.id != null ? String(found.id) : null
}
